import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/context/ToastContext";
import { syncPopups } from "@/lib/push";

/**
 * Lives in the app frame. A push that arrives while the CRM is open and visible is shown as an alert here (the system
 * pop-up is for when it is closed) and the bell refreshes at once; tapping a system pop-up while the CRM is open
 * sends the page to the right place. 
 */
export function PushListener() {
  const navigate = useNavigate();
  const { info } = useToast();

  // Pop-ups for things already read elsewhere go away when the CRM is opened or comes back to the front
  useEffect(() => {
    void syncPopups();
    const back = () => { if (document.visibilityState === "visible") void syncPopups(); };
    window.addEventListener("focus", back);
    document.addEventListener("visibilitychange", back);
    return () => { window.removeEventListener("focus", back); document.removeEventListener("visibilitychange", back); };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const m = e.data;
      if (!m || typeof m !== "object") return;
      if (m.type === "crm-push" && m.item) {
        // When the system already showed a pop-up, the page only refreshes the bell
        if (!m.shown) info(String(m.item.title || "Notification"), String(m.item.body || ""));
        window.dispatchEvent(new Event("crm:notification"));
      } else if (m.type === "crm-navigate" && typeof m.path === "string" && m.path.startsWith("/")) {
        window.dispatchEvent(new Event("crm:notification"));
        navigate(m.path);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [info, navigate]);

  return null;
}
